import { Form } from '@ant-design/compatible';
import { EyeOutlined } from '@ant-design/icons';
import { Col } from 'antd';
import ErrorHandler from 'common/error/components/error-handler';
import PermissionCheck from 'common/features/permission-check';
import LockMessage from 'common/lock/components/lock-message';
import ActionButtons from 'common/navigation/components/action-buttons';
import Partners from 'common/partners/components/main-partners';
import PublishSteps from 'common/publish-steps/containers/publish-steps-container';
import Spinner from 'common/spinner/components/spinner';
import CreateOrders from 'order/create/containers/create-orders-container';
import PropTypes from 'prop-types';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { Outlet, Route, Routes } from 'react-router-dom';
import { compose } from 'redux';
import styled from 'styled-components';
import TicketButton from 'tickets/ticket-button';
import {
  Button,
  Div,
  DropdownButton,
  Flex,
  IconText,
  Info,
  notification,
  Row,
  SubTitle,
  Title,
  TitleActions,
} from 'ui';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { severities } from 'utils/app-configs';
import { withRouter } from 'utils/withRouter';
import { referenceTypes } from '../../../utils/app-configs';
import { isObjectNullOrEmpty } from '../../../utils/helpers';
import Prompt from '../../../utils/prompt';
import {
  getFunctionalLocationInfoRequest,
  getFunctionalLocationInfoSuccess,
  resetInfo,
  resetSaveSuccess,
  saveFunctionalLocationInfoRequest,
} from '../containers/info-actions';
import Characteristics from './characteristics';
import Dates from './dates';
import General from './general';
import Organisation from './organisation';
import TaskList from './task-list';

const ShowMoreButton = styled(Button)`
  margin: 10px 0;
`;

const EditButton = styled(Button)`
  i {
    margin-left: -1px;
    margin-right: 5px;
  }
`;

const StyledIconText = styled(IconText)`
  text-transform: uppercase;
`;

const FunctionalLocationInfo = ({
  id,
  data,
  error,
  form,
  isSaving,
  isLoading,
  saveSuccess,
  intl,
  renderMenu,
  lock,
  history,
  partners,
  userStatuses,
  partnerTypes,
  taskLists = [],
  visitTypes,
}) => {
  const dispatch = useDispatch();
  const [isEditable, setIsEditable] = useState(qs.parse(history.location.search).edit === 'true');
  const [charsPageSize, setCharsPageSize] = useState(10);
  const [charsShowMoreVisible, setCharsShowMoreVisible] = useState(true);
  const [selectedTaskList, setSelectedTaskList] = useState({});
  const [isFormDirty, setIsFormDirty] = useState(false);

  useEffect(() => {
    dispatch(getFunctionalLocationInfoRequest({ id }));
    return () => dispatch(resetInfo());
  }, []);

  useEffect(() => {
    if (!isSaving && saveSuccess) {
      notification.success({
        message: intl.formatMessage({ id: 'type.functionallocation.SAVE_SUCCESS' }),
        placement: 'top',
      });
      setIsEditable(false);
      dispatch(resetSaveSuccess());
    }
  }, [saveSuccess, isSaving]);

  useDeepCompareEffect(() => {
    if (taskLists?.length && isObjectNullOrEmpty(selectedTaskList)) {
      setSelectedTaskList(taskLists[0]);
    }
  }, [taskLists, selectedTaskList]);

  const disabled = isSaving || isLoading || data.isInactive;
  const chars = form.getFieldValue('characteristics');

  const setFormDirty = () => {
    setIsFormDirty(true);
  };

  const toggleEditState = () => {
    setIsEditable(!isEditable);
  };

  const resetForm = () => {
    form.resetFields();
    toggleEditState();
  };

  const save = () => {
    form.validateFields((err, values) => {
      if (!err) {
        if (form.isFieldsTouched() || isFormDirty) {
          dispatch(saveFunctionalLocationInfoRequest({ data, values }));
        } else {
          resetForm();
        }
      }
    });
  };

  const showAllChars = () => {
    setCharsPageSize(chars?.length);
    setCharsShowMoreVisible(false);
  };

  const showLessChars = () => {
    setCharsPageSize(10);
    setCharsShowMoreVisible(true);
  };

  const handleVisitTypeChange = (visitTypeId) => {
    setSelectedTaskList(taskLists?.find(t => t?.id === visitTypeId));
  };

  const renderEditButton = () => {
    const editButtonIconClass = isSaving ?
      'fa-spinner fa-spin' : (lock && 'fa-lock') || 'fa-pencil';
    return (
      isEditable ?
        <DropdownButton
          disabled={!!lock}
          text={intl.formatMessage({ id: 'common.SAVE' })}
          iconClass={isSaving ? 'fa-spinner fa-spin' : 'fa-save'}
          onClick={save}
          subItems={[
            {
              text: intl.formatMessage({ id: 'common.CANCEL' }),
              onClick: resetForm,
            },
          ]}
        /> :
        <EditButton
          type='secondary'
          disabled={!!lock || isSaving}
          onClick={toggleEditState}
        >
          <i className={`fa ${editButtonIconClass}`} />
          {intl.formatMessage({ id: 'common.EDIT' })}
        </EditButton>
    );
  };
  const canEdit = false;

  form.getFieldDecorator('characteristics', {
    initialValue: data?.characteristics,
  });

  return (
    <Div marginBottom={20}>
      {renderMenu(
        <ActionButtons>
          {!data.isInactive ?
            <PermissionCheck permissionsList={['ordercreate']}>
              <Button
                type='secondary'
                onClick={() => history.push(`create-order/functionallocation/${id}`)}
              >
                <FormattedMessage id='common.CREATE_ORDER' />
              </Button>
            </PermissionCheck> :
            null}
          {isToggleEnabled('functionalLocationTicketFilter') ? <TicketButton id={id} /> : null}
        </ActionButtons>,
      )}
      <ErrorHandler type='notification' error={error} />
      <PublishSteps
        id={id}
        reloadData={() => dispatch(getFunctionalLocationInfoRequest({ id }))}
        onDataReloaded={getFunctionalLocationInfoSuccess}
        type={referenceTypes.functionallocation}
      />
      {isLoading ?
        <Spinner title={intl.formatMessage({ id: 'common.LOADING_INFO' })} />
        :
        <Info>
          <Form layout='vertical'>
            <Row>
              <Col span={24}>
                <Flex>
                  <Title>{data?.name}</Title>
                  {canEdit && isObjectNullOrEmpty(error) ?
                    <PermissionCheck permissionsList={['installedbasemanage']}>
                      <TitleActions>
                        <LockMessage lock={lock} />
                        {data?.isInactive ?
                          <StyledIconText
                            iconType='lock'
                            severity={severities.INACTIVE}
                            translationKey='functionalLocation.info.INACTIVE'
                          /> :
                          renderEditButton()
                        }
                      </TitleActions>
                    </PermissionCheck> :
                    null}
                </Flex>
              </Col>
            </Row>
            <Row type='flex' gutter={25} marginBottom={20}>
              <Col sm={12} xs={24}>
                <SubTitle>{intl.formatMessage({ id: 'common.titles.GENERAL' })}</SubTitle>
                <General
                  data={data}
                  isEditable={isEditable}
                  form={form}
                  disabled={disabled}
                  setFormDirty={setFormDirty}
                  userStatuses={userStatuses}
                />
              </Col>
              <Col sm={12} xs={24}>
                <Organisation
                  data={data}
                  isEditable={isEditable}
                  form={form}
                  disabled={disabled}
                />
                <Dates data={data} isEditable={isEditable} form={form} disabled={disabled} />
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <Partners partners={partners} types={partnerTypes} canEdit={false} />
              </Col>
            </Row>
            <Row type='flex' gutter={25} marginBottom={25} marginTop={25}>
              <Col sm={8} xs={24}>
                <Characteristics
                  characteristics={form.getFieldValue('characteristics')}
                  onChange={v => form.setFieldsValue({
                    characteristics: v,
                  })}
                  isEditable={isEditable}
                  disabled={disabled}
                  setFormDirty={setFormDirty}
                  pageSize={charsPageSize}
                />
              </Col>
              <Col span={24}>
                <Flex jcc>
                  {chars?.length > 10 ?
                    <ShowMoreButton
                      icon={<EyeOutlined />}
                      onClick={
                        charsShowMoreVisible
                          ? showAllChars
                          : showLessChars
                      }
                    >
                      {charsShowMoreVisible
                        ? intl.formatMessage({ id: 'functionalLocation.info.SHOW_ALL' })
                        : intl.formatMessage({ id: 'functionalLocation.info.SHOW_LESS' })}
                    </ShowMoreButton>
                    : null}
                </Flex>
              </Col>
            </Row>
            <Row type='flex'>
              <Col span={24}>
                <TaskList
                  data={selectedTaskList}
                  visitTypes={visitTypes}
                  onVisitTypeChange={handleVisitTypeChange}
                />
              </Col>
            </Row>
          </Form>
        </Info>
      }
      <Routes>
        <Route path={'create-order/:type/:ids'} element={<CreateOrders />} />
      </Routes>
      <Outlet />
      {isEditable &&
        <Prompt
          when={form.isFieldsTouched || isFormDirty}
          message={intl.formatMessage({ id: 'prompt.UNSAVED_CHANGES' })}
        />
      }
    </Div>
  );
};

FunctionalLocationInfo.propTypes = {
  id: PropTypes.string.isRequired,
  data: PropTypes.object,
  error: PropTypes.object,
  form: PropTypes.object,
  isSaving: PropTypes.bool,
  isLoading: PropTypes.bool,
  saveSuccess: PropTypes.bool,
  intl: PropTypes.object,
  renderMenu: PropTypes.func.isRequired,
  lock: PropTypes.object,
  history: PropTypes.object.isRequired,
  partners: PropTypes.array,
  userStatuses: PropTypes.array,
  partnerTypes: PropTypes.array,
  taskLists: PropTypes.array,
  visitTypes: PropTypes.array,
};

export default compose(
  Form.create(),
  withRouter,
  injectIntl,
)(FunctionalLocationInfo);
